import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorRecord } from 'src/hooks/useForm';
import JmrService from 'src/services/jmr/jmrService';
import JobsService from 'src/services/jobsService';
import IncidentService from 'src/services/incident/incidentService';
import PeopleApiService from 'src/services/peopleApiService';
import CapacityUsageService from 'src/services/capacityUsage/capacityUsageService';
import { Site } from 'src/store/siteSlice';
import {
  IdentifyBodyLocationEnum,
  JmrFlowTypes,
  JmrJobTypes,
  JmrPageProgress,
  JmrResult,
  jmrResultToTableRow,
  TableDataRow,
} from 'src/utils/jmrUtils';
import { datePickerStringToDateObj } from 'src/utils/dateUtils';
import EmailDistributionService from 'src/services/emailDistribution/emailDistributionService';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';
import { ACCOMMODATION_STATUS } from 'src/config/AccommodationStatusForJmrUpdate';
import { AustinCase } from 'src/services/incident/types';
import { INDEFINITE_RESTRICTIONS_ID } from 'src/config/AccommodationStatusId';
import { ApiCallStatus } from 'src/store/type';
import {
  regionTaskListToRestrictionItemTasks,
  RestrictionListItem,
  RestrictionListParentTask,
  RestrictionListTask,
} from 'src/utils/restrictionUtils';
import { ApiVersion } from 'src/types/commonTypes';
import type { RootState } from './store';
import { SiteJobCapacityUsageData } from 'src/services/capacityUsage/types/siteJobCapacityUsageData';
import { AccommodationFields, JmrPayload, YesNoEnum } from 'src/services/jmr/types';
import ProcessingMessage from 'src/types/processingMessage';
import { EmailSettings } from 'src/services/emailDistribution/types/emailSettings';
import TaskService from 'src/services/tasks/taskService';
import { RegionTask } from 'src/services/tasks/types';
import { MeasureUnitValues } from 'src/services/tasks/constants';

// **** Types **** //

type FetchAustinCasesPayload = { alias: string } | { placementId: number };

export interface JmrTask extends RegionTask {
  restrictedValue: string;
  isExpanded?: boolean;
  bodyLocationValue?: IdentifyBodyLocationEnum;
}

export interface BackgroundFormData {
  alias: string;
  fullName: string;
  siteLocation: string;
  site: Site;
  job: string;
  employeeID: string;
  personId: string;
  country: string;
  shift: string;
  shiftLength: number;
  caseID: string;
  caseSource: string;
  restrictionStartDate: string;
  restrictionEndDate: string;
  cannotWorkNormalShiftHrs: boolean;
  cannotWorkMoreThan40Hrs: boolean;
  indefiniteRestrictions: boolean;
  providerPlacedOffWork: boolean;
  limitedHoursPerDay: string;
  managerName: string;
  managerAlias: string;
  limitedHoursPerWeek: string;
  caseIdValid: boolean;
  accommodationStatus: string;
}

export interface BackgroundFormFieldLength {
  maxAliasLength: number;
  maxFullNameLength: number;
  maxJobLength: number;
  maxEmployeeIdLength: number;
  maxShiftCodeLength: number;
  maxManagerAliasLength: number;
  maxManagerNameLength: number;
  maxCaseIdLength: number;
}

interface JmrFilter {
  searchField: string;
  currentPage: number;
  totalPages: number;
  jobType: JmrJobTypes;
  perPage: number;
}

export interface ApprovalFormData {
  emailRecipients: string;
  emailContent: string;
}

export type JmrPageState = {
  pageProgress: JmrPageProgress;
  isCancelModalOpen: boolean;
  backgroundFormData: BackgroundFormData;
  backgroundFormErrors: ErrorRecord<BackgroundFormData>;
  backgroundFormFieldLengths: BackgroundFormFieldLength;
  regionTasks: RegionTask[];
  consolidatedRegionTasks: (RestrictionListTask | RestrictionListParentTask)[];
  selectedTaskNames: string[];
  selectedTasks: JmrTask[];
  restrictionsFormErrors: any;
  jmrResults: TableDataRow[];
  jmrFilter: JmrFilter;
  approvalFormData: ApprovalFormData;
  approvalFormErrors: ErrorRecord<ApprovalFormData>;
  isResultsPageValid: boolean;
  isRestrictionsModalOpen?: string;
  createdAccommodationId: number;
  jmrFlowType: JmrFlowTypes;
  placementId: number;
  accommodationSavingStatus: ApiCallStatus;
  austinCases: AustinCase[];
  searchedAlias: string;
  isLoadingPAPI: boolean;
  isLoadingIncident: boolean;
  isLoadingGetPlacementDetails: boolean;
  isAUSTINDown?: boolean;
  areAustinCasesFetched: boolean;
  processingMessages: ProcessingMessage[];
  externalCaseId: string;
  accommodationType: string;
  showCaseIdRequiredText: boolean;
  dpFeatureFlag: boolean;
  isProviderPlacedOffWorkModalOpen: boolean;
  tldAlertModal: {
    hasBeenOpened: boolean;
    resultId: string;
    jobId: number;
  };
  restrictionListItems: RestrictionListItem[];
  isRestrictionsPageReadOnly: boolean;
  isUsingNewExperience: boolean;
};

// **** Functions **** //

/**
 * Fetch a list of Austin cases (incidents). Note that if we are "updating" the accommodation (placement), the "alias"
 * in the background page may not have access to the site (and by default not have access to the incidences) and we will
 * need to fetch the incident specific to that accommodation instead.
 */
export const fetchAustinCases = createAsyncThunk(
  'newJmr/fetchAustinCases',
  async (payload: FetchAustinCasesPayload, { dispatch }) => {
    dispatch(actions.clearAustinCases());
    if ('alias' in payload) {
      const resp = await IncidentService.fetchForAssociate(payload.alias);
      return resp?.data ?? [];
    } else if ('placementId' in payload) {
      const resp = await IncidentService.fetchForAccommodation(payload.placementId);
      return [resp?.data];
    }
    return [];
  },
);

/**
 * Get the employeee info
 */
export const getEmployeeInfo = createAsyncThunk('newJmr/getEmployeeInfo', async (employeeLogin: string) => {
  const { data } = await PeopleApiService.getEmployeeInfo(employeeLogin);
  return data;
});

/**
 * Get tasks of a region by region Id
 */
export const getRegionTasksById = createAsyncThunk('newJmr/getRegionTasksById', async (regionId: number) => {
  return TaskService.fetchRegionTasksByRegionId(regionId);
});

/**
 * Will result in setting the dpFeatureFlag to true if this async thunk action is dispatched.
 */
export const setDecisionPortalFeatureFlagInNewJmrSlice = createAsyncThunk(
  'newJmr/setDecisionPortalFeatureInNewJmrSlice',
  async () => true,
);

/**
 * Fetch a job by the job-id
 */
export const getJob = createAsyncThunk('newJmr/getJob', async (jobId: number) => {
  const { data } = await JobsService.getJobById(jobId);
  return data;
});

/**
 * Create an accommodation (placement)
 */
export const createAccommodation = createAsyncThunk<any, void, { state: RootState }>(
  'newJmr/createAccommodation',
  async (_, { getState }) => {
    const state = getState();
    const backgroundFormData: BackgroundFormData = state.newJmrPage.backgroundFormData;
    const jmrFlowType = state.newJmrPage.jmrFlowType;
    const placementId = state.newJmrPage.placementId;
    const selectedTasks: JmrTask[] = state.newJmrPage.selectedTasks;
    const jmrResults: TableDataRow[] = state.newJmrPage.jmrResults.filter(
      (jmrResult: TableDataRow) => jmrResult.isChecked,
    );
    const email: string = state.newJmrPage.approvalFormData.emailRecipients;
    // Data from date picker is recorded in local date time
    // this converts it to utc.
    const utcRestrictionStartDate = datePickerStringToDateObj(
      backgroundFormData.restrictionStartDate,
    )?.toISOString() as string;
    const utcRestrictionEndDate =
      backgroundFormData.site.participatingInDecisionHubPilot && backgroundFormData.indefiniteRestrictions
        ? ''
        : (datePickerStringToDateObj(backgroundFormData.restrictionEndDate)?.toISOString() as string);
    // Initialize data
    const accommodationData: AccommodationFields = {
      associateFullName: backgroundFormData.fullName,
      requesterLogin: state.user.alias,
      startAt: utcRestrictionStartDate,
      endAt: utcRestrictionEndDate,
      managerFullName: backgroundFormData.managerName,
      managerAlias: backgroundFormData.managerAlias,
      employeeId: backgroundFormData.employeeID,
      personId: backgroundFormData.personId,
      job: backgroundFormData.job,
      login: backgroundFormData.alias,
      fc: backgroundFormData.siteLocation,
      siteId: backgroundFormData.site.id,
      accommodationType: backgroundFormData.caseSource.toUpperCase(),
      externalCaseId: backgroundFormData.caseID,
      weeklyLimitedHours: +backgroundFormData.limitedHoursPerWeek,
      modifiedShiftLength: backgroundFormData.limitedHoursPerDay
        ? +backgroundFormData.limitedHoursPerDay
        : +backgroundFormData.shiftLength,
      canWorkNormalShiftHours: !backgroundFormData.cannotWorkNormalShiftHrs,
      canWorkMoreThan40Hours: !backgroundFormData.cannotWorkMoreThan40Hrs,
      shiftCode: backgroundFormData.shift,
      normalShiftLength: +backgroundFormData.shiftLength,
      workRestrictions: selectedTasks.map((t) => ({
        taskId: t.taskId,
        hoursRestricted: t.measureUnit === MeasureUnitValues.Bool ? 'No' : t.restrictedValue,
        taskName: t.label,
        bodyLocation: t.bodyLocationValue,
      })),
      allocatedJobShifts: jmrResults.map((result) => ({
        jobId: result.jobId,
        shiftId: result.shiftId,
        allocatedHours: +result.totalHours,
        jobName: result.name,
        shiftName: result.shiftCode,
        appliedModifications: (result.conflicts || []).flatMap((c) =>
          (c.appliedModifications || []).map((m) => ({ taskId: c.taskId, modificationId: m.id })),
        ),
      })),
    };
    if (jmrFlowType === JmrFlowTypes.CreateNewAccommodation) {
      const { data } = await JmrService.createAccommodation({
        accommodation: accommodationData,
        emailRecipients: email,
      });
      return data;
    } else {
      const updatePayload = {
        accommodation: {
          ...accommodationData,
          id: placementId,
        } as any,
        emailRecipients: email,
      };
      if (jmrFlowType === JmrFlowTypes.NewRestrictions) {
        updatePayload.accommodation.accommodationStatus = ACCOMMODATION_STATUS.NEW_RESTRICTIONS_RECEIVED;
        const { data } = await JmrService.updateAccommodationRestrictions(updatePayload);
        return data;
      } else if (jmrFlowType === JmrFlowTypes.AlternativePlacementRecommended) {
        updatePayload.accommodation.accommodationStatus = ACCOMMODATION_STATUS.ALTERNATIVE_PLACEMENT_RECOMMENDED;
        const { data } = await JmrService.recommendAlternativePlacement(updatePayload);
        return data;
      } else if (jmrFlowType === JmrFlowTypes.CompleteTransfer) {
        updatePayload.accommodation.accommodationStatus = ACCOMMODATION_STATUS.TRANSFER_PENDING;
        const { data } = await JmrService.completeTransferPlacement(updatePayload);
        return data;
      } else if (jmrFlowType === JmrFlowTypes.UpdateDates) {
        updatePayload.accommodation.accommodationStatus = ACCOMMODATION_STATUS.ALTERNATIVE_PLACEMENT_RECOMMENDED;
        const { data } = await JmrService.updateRestrictionDates(updatePayload);
        return data;
        // For both create or update PPOW
      } else if (jmrFlowType === JmrFlowTypes.ProviderPlacedOffWork) {
        const { data } = await JmrService.providerPlacedOffWork(updatePayload);
        return data;
      }
    }
  },
);

/**
 * Get placement detail data
 */
export const getPlacementDetails = createAsyncThunk('newJmr/getPlacementDetails', async (accommodationId: number) => {
  const { data } = await JmrService.getAccommodation(accommodationId);
  return data;
});

/**
 * Get email settings by site
 */
export const getEmailSettingsForSite = createAsyncThunk<EmailSettings, void, { state: RootState }>(
  'newJmr/getEmailSettings',
  async (_, { getState }) => {
    const { backgroundFormData } = getState().newJmrPage;
    const { data } = await EmailDistributionService.getForSite(backgroundFormData.site?.id);
    return data;
  },
);

/**
 * Generate job matches
 */
export const generateJobMatches = createAsyncThunk<
  {
    jmrResultsData: JmrResult[];
    jobCapacityUsageData: SiteJobCapacityUsageData[];
  },
  ApiVersion | undefined,
  { state: RootState }
>('newJmr/generateJobMatches', async (apiVersion = 'v1', { getState }) => {
  const newJmrPageState = getState().newJmrPage;
  const { backgroundFormData: formData } = newJmrPageState;
  const payload: JmrPayload = {
    location: formData?.siteLocation,
    siteId: formData.site?.id,
    shiftLength: formData.cannotWorkNormalShiftHrs ? +formData.limitedHoursPerDay : +formData.shiftLength,
    taskRestrictions: newJmrPageState.selectedTasks?.map((t) => ({
      taskId: t.taskId,
      measureUnit: t.measureUnit,
      restrictedValue: t.measureUnit === MeasureUnitValues.Bool ? YesNoEnum.No : +t.restrictedValue,
    })),
  };
  const { data: jmrResultsData } =
    apiVersion === 'v2' ? await JmrService.generateJobMatchesV2(payload) : await JmrService.generateJobMatches(payload);
  const { data: jobCapacityUsageData } = await CapacityUsageService.getCapacityUsageForSite(payload.siteId);
  return {
    jmrResultsData,
    jobCapacityUsageData,
  };
});

/**
 * Should end date be emptied out.
 */
const shouldEndDateBeEmptiedOut = (
  currentAccommodationStatusId: number,
  doesDateRepresentIndefiniteRestrictions: boolean,
) => {
  return currentAccommodationStatusId === INDEFINITE_RESTRICTIONS_ID || doesDateRepresentIndefiniteRestrictions;
};

// **** Setup the "Initial State" and "Slice" **** //

const initialBackgroundFormData = {
  alias: '',
  fullName: '',
  job: '',
  employeeID: '',
  shift: '',
  caseID: '',
  caseSource: '',
  managerName: '',
  managerAlias: '',
  cannotWorkNormalShiftHrs: false,
  cannotWorkMoreThan40Hrs: false,
  indefiniteRestrictions: false,
  providerPlacedOffWork: false,
};

const backgroundFormFieldLengthValues: BackgroundFormFieldLength = {
  maxAliasLength: 255,
  maxFullNameLength: 150,
  maxJobLength: 150,
  maxEmployeeIdLength: 150,
  maxShiftCodeLength: 150,
  maxManagerAliasLength: 150,
  maxManagerNameLength: 150,
  maxCaseIdLength: 255,
};

const initialState: JmrPageState = {
  pageProgress: JmrPageProgress.Background,
  isCancelModalOpen: false,
  backgroundFormData: initialBackgroundFormData as BackgroundFormData,
  backgroundFormErrors: {},
  backgroundFormFieldLengths: backgroundFormFieldLengthValues,
  regionTasks: [],
  consolidatedRegionTasks: [],
  selectedTaskNames: [],
  selectedTasks: [],
  restrictionsFormErrors: {},
  jmrResults: [],
  jmrFilter: {
    searchField: '',
    currentPage: 1,
    totalPages: 1,
    perPage: DEFAULT_TABLE_PAGE_SIZE,
    jobType: JmrJobTypes.ProcessPath,
  },
  approvalFormData: { emailContent: '', emailRecipients: '' },
  approvalFormErrors: {},
  isResultsPageValid: false,
  isRestrictionsModalOpen: undefined,
  createdAccommodationId: -1,
  jmrFlowType: JmrFlowTypes.CreateNewAccommodation,
  placementId: 0,
  accommodationSavingStatus: ApiCallStatus.Idle,
  austinCases: [],
  searchedAlias: '',
  isLoadingPAPI: false,
  isLoadingIncident: false,
  isLoadingGetPlacementDetails: false,
  isAUSTINDown: false,
  areAustinCasesFetched: false,
  processingMessages: [],
  externalCaseId: '',
  accommodationType: '',
  showCaseIdRequiredText: false,
  // The userSlice is the source for this. In order to share that value with this newJmrSlice,
  // app.tsx dispatches an async thunk action. This is done cause we can't use another redux
  // slice's state directly in a different redux slice.
  dpFeatureFlag: false,
  isProviderPlacedOffWorkModalOpen: false,
  tldAlertModal: {
    hasBeenOpened: false,
    resultId: '',
    jobId: -1,
  },
  restrictionListItems: [],
  isRestrictionsPageReadOnly: false,
  isUsingNewExperience: true,
};

// Initialize slice "New JMR Page Slice"
const { reducer, actions } = createSlice({
  name: 'newJmrPageSlice',
  initialState,
  reducers: {
    clearAustinCases: (state) => {
      state.austinCases = [];
      state.areAustinCasesFetched = false;
    },
    clearProcessingMessagesData: (state) => {
      state.processingMessages = [];
      state.externalCaseId = '';
      state.accommodationType = '';
    },
    setBackgroundFormData: (state, action: PayloadAction<Partial<BackgroundFormData>>) => {
      // Ensure data fields have consistent values. when the cannotWorkNormalShiftHrs
      // checkbox is unchecked, the limitedHoursPerDay value should be cleared.
      if (!action.payload.cannotWorkNormalShiftHrs) {
        // Set the value to undefined instead of null or "", so that +limitedHoursPerDay
        // evaluates to NaN if never set on UI. NaN becomes null when submitted to backend
        // from UI correctly, but null or "" becomes 0, distorting the user intention
        action.payload.limitedHoursPerDay = undefined;
      }
      // Same for cannotWorkMoreThan40Hrs and limitedHoursPerWeek
      if (!action.payload.cannotWorkMoreThan40Hrs) {
        action.payload.limitedHoursPerWeek = undefined;
      }
      state.backgroundFormData = { ...state.backgroundFormData, ...action.payload };
    },
    setBackgroundFormErrors: (state, action) => {
      state.backgroundFormErrors = action.payload;
    },
    resetBackgroundFormData: (state) => {
      // reset background form
      state.backgroundFormData = initialState.backgroundFormData;
    },
    setPageProgress: (state, action) => {
      state.pageProgress = action.payload;
    },
    setIsCancelModalOpen: (state, action) => {
      state.isCancelModalOpen = action.payload;
    },
    setSelectedTaskNames: (state, action) => {
      state.selectedTaskNames = action.payload;
    },
    setSelectedTasks: (state, action) => {
      state.selectedTasks = action.payload;
    },
    setRestrictionsFormErrors: (state, action) => {
      state.restrictionsFormErrors = action.payload;
    },
    setJmrResults: (state, action) => {
      state.jmrResults = action.payload;
    },
    setJmrFilter: (state, action) => {
      state.jmrFilter = action.payload;
    },
    setApprovalFormData: (state, action) => {
      state.approvalFormData = action.payload;
    },
    setApprovalFormErrors: (state, action) => {
      state.approvalFormErrors = action.payload;
    },
    setIsResultsPageValid: (state, action) => {
      state.isResultsPageValid = action.payload;
    },
    setCreatedAccommodationId: (state, action) => {
      state.createdAccommodationId = action.payload;
    },
    setPlacementId: (state, action) => {
      state.placementId = action.payload;
    },
    setJmrFlowType: (state, action) => {
      state.jmrFlowType = action.payload;
    },
    setSearchedAlias: (state, action) => {
      state.searchedAlias = action.payload;
    },
    setIsAUSTINDown: (state, action) => {
      state.isAUSTINDown = action.payload;
    },
    setShowCaseIdRequiredText: (state, action) => {
      state.showCaseIdRequiredText = action.payload;
    },
    setIsProviderPlacedOffWorkModalOpen: (state, action) => {
      state.isProviderPlacedOffWorkModalOpen = action.payload;
    },
    setAccommodationSavingStatus: (state, action) => {
      state.accommodationSavingStatus = action.payload;
    },
    setIsTldAlertModalOpen: (
      state,
      action: PayloadAction<{
        open: boolean;
        hasBeenOpened?: boolean;
        jobId?: number;
        resultId?: string;
      }>,
    ) => {
      state.tldAlertModal = {
        ...state.tldAlertModal,
        ...(action.payload.open
          ? {
              open: true,
              hasBeenOpened: true,
              jobId: action.payload?.jobId || -1,
              resultId: action.payload?.resultId || '',
            }
          : {
              open: false,
              jobId: -1,
              resultId: '',
            }),
      };
    },
    resetTldAlertModal: (state) => {
      state.tldAlertModal = {
        hasBeenOpened: false,
        jobId: -1,
        resultId: '',
      };
    },
    setRestrictionListItems: (state, action) => {
      state.restrictionListItems = action.payload;
    },
    setRestrictionListItem: (state, action) => {
      const newList = state.restrictionListItems;
      newList[action.payload.index] = action.payload.item;
      state.restrictionListItems = newList;
    },
    setConsolidatedRegionTasks: (state, action) => {
      state.consolidatedRegionTasks = action.payload;
    },
    setIsRestrictionsPageReadOnly: (state, action) => {
      state.isRestrictionsPageReadOnly = action.payload;
    },
    deleteRestrictionListItem: (state, action) => {
      const index = action.payload;
      const items = state.restrictionListItems;
      state.restrictionListItems = items.filter((_, i) => i !== index);
    },
    setIsUsingNewExperience: (state, action: PayloadAction<boolean>) => {
      state.isUsingNewExperience = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRegionTasksById.fulfilled, (state, { payload }) => {
      const regionTasks = payload.map((t: RegionTask) => ({
        ...t,
        isExpanded: true,
      }));
      state.regionTasks = regionTasks;
      state.consolidatedRegionTasks = regionTaskListToRestrictionItemTasks(regionTasks);
    });
    builder.addCase(fetchAustinCases.pending, (state) => {
      state.isLoadingIncident = true;
      state.areAustinCasesFetched = false;
    });
    builder.addCase(fetchAustinCases.fulfilled, (state, { payload }) => {
      state.areAustinCasesFetched = true;
      state.isAUSTINDown = false;
      state.isLoadingIncident = false;
      state.austinCases = payload;
    });
    builder.addCase(fetchAustinCases.rejected, (state) => {
      state.areAustinCasesFetched = false;
      state.isAUSTINDown = true; // This allows us to complete JMR flow when Austin is down
      state.isLoadingIncident = false;
    });
    builder.addCase(getEmployeeInfo.pending, (state) => {
      state.isLoadingPAPI = true;
    });
    builder.addCase(getEmployeeInfo.fulfilled, (state, { payload }) => {
      state.backgroundFormData.fullName = `${payload.basicInfo?.firstName} ${payload.basicInfo?.lastName}`;
      state.backgroundFormData.employeeID = payload.basicInfo?.legacyEmployeeId;
      state.backgroundFormData.personId = payload.basicInfo?.personId;
      state.backgroundFormData.job = payload.basicInfo?.businessTitle.trim();
      if (!state.backgroundFormData.job) {
        state.backgroundFormData.job = payload.basicInfo?.jobProfileName.trim();
      }
      state.backgroundFormData.managerAlias = payload.basicInfo?.managerLogin;
      state.backgroundFormData.managerName = payload.basicInfo?.managerName;
      state.backgroundFormData.shift = payload.job?.workScheduleId.trim();
      state.backgroundFormData.siteLocation = payload.workLocation?.buildingCode;
      state.isLoadingPAPI = false;
    });
    builder.addCase(getEmployeeInfo.rejected, (state) => {
      state.backgroundFormData.fullName = '';
      state.backgroundFormData.employeeID = '';
      state.backgroundFormData.personId = '';
      state.backgroundFormData.job = '';
      state.backgroundFormData.managerAlias = '';
      state.backgroundFormData.managerName = '';
      state.backgroundFormData.shift = '';
      state.backgroundFormData.siteLocation = '';
      state.isLoadingPAPI = false;
    });
    builder.addCase(generateJobMatches.fulfilled, (state, { payload }) => {
      const { jmrResultsData, jobCapacityUsageData } = payload;
      const capacityUsageMap = new Map(
        jobCapacityUsageData.map((jobCapacityUsage) => [jobCapacityUsage.jobId, jobCapacityUsage]),
      );
      state.jmrResults = jmrResultsData
        .map((jmrResult: JmrResult) => jmrResultToTableRow(jmrResult, capacityUsageMap, state.isUsingNewExperience))
        .flat(1);
    });
    builder.addCase(getJob.fulfilled, (state, { payload }) => {
      state.jmrResults = state.jmrResults.map((jmrResult: TableDataRow) =>
        jmrResult.jobId === payload.id ? { ...jmrResult, jobRequirements: payload.jobRequirements } : jmrResult,
      );
    });
    builder.addCase(createAccommodation.fulfilled, (state, { payload }) => {
      if (payload) {
        state.createdAccommodationId = payload.id;
        state.accommodationSavingStatus = ApiCallStatus.Success;
        state.processingMessages = payload.processingMessages;
        state.externalCaseId = payload.externalCaseId;
        state.accommodationType = payload.accommodationType;
        // reset background form
        state.backgroundFormData = initialState.backgroundFormData;
      }
    });
    builder.addCase(createAccommodation.pending, (state) => {
      state.accommodationSavingStatus = ApiCallStatus.Pending;
    });
    builder.addCase(createAccommodation.rejected, (state) => {
      state.accommodationSavingStatus = ApiCallStatus.Error;
    });
    builder.addCase(getPlacementDetails.pending, (state) => {
      state.isLoadingGetPlacementDetails = true;
    });
    builder.addCase(getPlacementDetails.rejected, (state) => {
      state.isLoadingGetPlacementDetails = false;
    });
    builder.addCase(getPlacementDetails.fulfilled, (state, { payload }) => {
      const doesDateRepresentIndefiniteRestrictions =
        state.dpFeatureFlag && payload.endAt.split('T')[0] === '3000-02-02';
      state.isLoadingGetPlacementDetails = false;
      state.backgroundFormData = {
        ...state.backgroundFormData,
        alias: payload.login,
        fullName: payload.associateFullName,
        siteLocation: payload.fc,
        job: payload.job,
        employeeID: payload.employeeId,
        personId: payload.personId,
        shift: payload.shiftCode,
        shiftLength: payload.normalShiftLength,
        // If cannot work normal shift hours checkbox is unchecked, there should not be a value for its text field
        limitedHoursPerDay: payload.canWorkNormalShiftHours ? undefined : payload.modifiedShiftLength,
        managerAlias: payload.managerAlias,
        managerName: payload.managerFullName,
        caseSource: payload.accommodationType,
        caseID: payload.externalCaseId,
        caseIdValid: payload.caseIdValid,
        cannotWorkNormalShiftHrs: !payload.canWorkNormalShiftHours,
        cannotWorkMoreThan40Hrs: !payload.canWorkMoreThan40Hours,
        // This value controls the indefinite restrictions checkbox
        indefiniteRestrictions: doesDateRepresentIndefiniteRestrictions,
        // If cannot work more than 40 hours checkbox is unchecked, there should not be a value for its text field
        limitedHoursPerWeek: payload.canWorkMoreThan40Hours ? undefined : payload.weeklyLimitedHours,
        accommodationStatus: payload.accommodationStatus.status,
      };
      if (
        [
          JmrFlowTypes.AlternativePlacementRecommended,
          JmrFlowTypes.UpdateDates,
          JmrFlowTypes.CompleteTransfer,
        ].includes(state.jmrFlowType)
      ) {
        if (JmrFlowTypes.CompleteTransfer !== state.jmrFlowType) {
          state.backgroundFormData = {
            ...state.backgroundFormData,
            site: payload.site,
            // End date will be empty if current status is indefinite restrictions.
            // Refer: https://issues.amazon.com/issues/P113097040
            restrictionEndDate: shouldEndDateBeEmptiedOut(
              payload.accommodationStatusId,
              doesDateRepresentIndefiniteRestrictions,
            )
              ? ''
              : payload.endAt.split('T')[0],
            restrictionStartDate: payload.startAt.split('T')[0],
          };
        }
        if (
          JmrFlowTypes.CompleteTransfer !== state.jmrFlowType ||
          (JmrFlowTypes.CompleteTransfer === state.jmrFlowType &&
            payload.previousSite.regionId === payload.site.regionId)
        ) {
          state.selectedTasks = payload.workRestrictions.map((restriction: any) => ({
            taskId: restriction.taskId,
            label: restriction.task.label,
            restrictedValue: restriction.hoursRestricted,
            measureUnit: restriction.task.measureUnit.name,
            bodyLocationValue: restriction.bodyLocation,
          }));
          state.selectedTaskNames = payload.workRestrictions.map((restriction: any) => restriction.task.label);
        }
      }
    });
    builder.addCase(getEmailSettingsForSite.fulfilled, (state, { payload }) => {
      const backgroundFormData: BackgroundFormData = state.backgroundFormData;
      const managerEmail = `${backgroundFormData.managerAlias}@amazon.com`;
      let emailRecipients = payload?.placementEmailRecipients ?? '';
      if (payload?.supervisorsForPlacementEmail) {
        emailRecipients =
          emailRecipients.trim().length > 0
            ? `${managerEmail};${payload?.placementEmailRecipients}`
            : `${managerEmail}`;
      }
      state.approvalFormData.emailRecipients = emailRecipients;
    });
    // Can be removed once decision portal is live.
    builder.addCase(setDecisionPortalFeatureFlagInNewJmrSlice.fulfilled, (state, { payload }) => {
      state.dpFeatureFlag = payload;
    });
  },
});

// **** Export **** //

export const {
  clearProcessingMessagesData,
  setPageProgress,
  setIsCancelModalOpen,
  setBackgroundFormData,
  setBackgroundFormErrors,
  resetBackgroundFormData,
  setSelectedTaskNames,
  setSelectedTasks,
  setConsolidatedRegionTasks,
  setRestrictionsFormErrors,
  setJmrResults,
  setJmrFilter,
  setApprovalFormData,
  setIsResultsPageValid,
  setCreatedAccommodationId,
  setApprovalFormErrors,
  setPlacementId,
  setJmrFlowType,
  setSearchedAlias,
  setIsAUSTINDown,
  setShowCaseIdRequiredText,
  setIsProviderPlacedOffWorkModalOpen,
  setAccommodationSavingStatus,
  setIsTldAlertModalOpen,
  resetTldAlertModal,
  setRestrictionListItems,
  setRestrictionListItem,
  deleteRestrictionListItem,
  setIsRestrictionsPageReadOnly,
  setIsUsingNewExperience,
} = actions;

export default reducer;
